import { useState } from 'react';
import Modal from './Modal';
import { useCurrentOrganization } from 'src/contexts/OrganizationContext';
import { apiPost } from 'src/utils/api';
import { MatchPatientBody } from 'src/shared/types';
import Input from '../inputs/Input';
import SelectInput from '../inputs/SelectInput';
import { MagnifyingGlassIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';

export type PatientLookupResult = {
  patientId: string;
  displayName?: string;
  birthDate?: string;
};

type PatientLookupModalProps = {
  onClose: () => void;
  onSelectPatient: (patientId: string) => void;
};

const PatientLookupModal = ({ onClose, onSelectPatient }: PatientLookupModalProps) => {
  const organization = useCurrentOrganization();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [legalSex, setLegalSex] = useState('male');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [ssn, setSsn] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<PatientLookupResult[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults([]);
    setSelectedId(null);

    if (!organization) {
      setError('Organization not found');
      return;
    }

    setLoading(true);
    try {
      const matchBody: MatchPatientBody = {
        firstName,
        lastName,
        gender: legalSex,
        birthDate,
        ssn: ssn || '',
      };
      if (phone) matchBody.phone = phone;
      if (email) matchBody.email = email;

      const response = await apiPost(
        `/api/${organization.id}/ehr/match-patient`,
        matchBody
      );

      if (!response.ok) {
        if (response.status === 404) {
          setResults([]);
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Match failed');
      }

      const data = await response.json();
      if (data.tempPatientId) {
        setResults([]);
        return;
      }

      const patientId = data.patientId;
      const patients: PatientLookupResult[] = patientId ? [{
        patientId: data.patientId,
        displayName: data.displayName,
        birthDate: data.birthDate,
      }] : [];
      setResults(patients);
      if (patientId) setSelectedId(patientId);
      else setSelectedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while searching.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (selectedId) {
      onSelectPatient(selectedId);
      onClose();
    }
  };

  return (
    <Modal title="Patient Lookup" onClose={onClose}>
      <form onSubmit={handleSearch} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            type="text"
            label="First Name"
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <Input
            type="text"
            label="Last Name"
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            type="date"
            label="Date of Birth"
            placeholder="YYYY-MM-DD"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            required
          />
          <SelectInput
            label="Legal Sex"
            placeholder="Select legal sex"
            value={legalSex}
            onChange={(e) => setLegalSex(e.target.value)}
            options={[
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
            ]}
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            type="text"
            label="SSN"
            placeholder="000-00-0000"
            value={ssn}
            onChange={(e) => setSsn(e.target.value)}
            required
          />
          <Input
            type="tel"
            label="Phone"
            placeholder="123-456-7890"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required={false}
          />
        </div>
        <div className="sm:col-span-2">
          <Input
            type="email"
            label="Email"
            placeholder="patient@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required={false}
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-2.5 rounded-lg text-sm font-medium bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white transition-colors"
        >
          <MagnifyingGlassIcon className="h-5 w-5" />
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {results.length > 0 && (
        <div className="mt-6 border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Results</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DOB
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient ID
                  </th>
                  <th scope="col" className="px-4 py-2 w-12" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((row) => (
                  <tr
                    key={row.patientId}
                    onClick={() => setSelectedId(row.patientId)}
                    className={`cursor-pointer hover:bg-gray-50 ${selectedId === row.patientId ? 'bg-brand-50' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900">{row.displayName ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.birthDate ?? '—'}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-600">{row.patientId}</td>
                    <td className="px-4 py-3">
                      <input
                        type="radio"
                        name="patient-select"
                        checked={selectedId === row.patientId}
                        onChange={() => setSelectedId(row.patientId)}
                        className="h-4 w-4 text-brand-600 border-gray-300 focus:ring-brand-500"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={handleSelect}
              disabled={!selectedId}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-600 hover:bg-brand-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-colors"
            >
              Select patient
            </button>
          </div>
        </div>
      )}

      {!loading && results.length === 0 && (firstName || lastName || birthDate) && (
        <div className="mt-6 border-t border-gray-200 pt-4">
          <p className="text-sm text-gray-500">No matching patients found. Try adjusting your search.</p>
        </div>
      )}
    </Modal>
  );
};

export default PatientLookupModal;
